import React, { FC, useCallback, useRef, useState } from 'react';
import {
  AllieChevronBackOutline,
  AllieChevronForwardOutline,
} from '../../../../styling/icons';
import {
  ScrollBlock,
  ScrollToggleButton,
  ScrollViewport,
} from '../../AllieDataGrid.styles';

const BUTTON_HALF_HEIGHT = 18;
const SCROLL_BTN_Y_VAR = '--scroll-btn-y';

export interface DataGridHorizontalScrollProps {
  scrollForwardAriaLabel: string;
  scrollBackAriaLabel: string;
  children: React.ReactNode;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const DataGridHorizontalScroll: FC<DataGridHorizontalScrollProps> = ({
  scrollForwardAriaLabel,
  scrollBackAriaLabel,
  children,
}) => {
  const blockRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const pointerRafRef = useRef<number | null>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const cancelPendingPointerRaf = useCallback(() => {
    if (pointerRafRef.current === null) return;
    cancelAnimationFrame(pointerRafRef.current);
    pointerRafRef.current = null;
  }, []);

  const measureScroll = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;

    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const overflow = maxScrollLeft > 1;
    setHasOverflow(overflow);
    setScrolledToEnd(overflow && el.scrollLeft >= maxScrollLeft - 1);
  }, []);

  const setBlockNode = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) {
        cancelPendingPointerRaf();
      }
      blockRef.current = el;
    },
    [cancelPendingPointerRaf]
  );

  const setViewportNode = useCallback(
    (el: HTMLDivElement | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      viewportRef.current = el;

      if (!el) return;

      const resizeObserver = new ResizeObserver(measureScroll);
      resizeObserver.observe(el);

      let observedChild: Element | null = null;

      const syncObservedChild = () => {
        const nextChild = el.firstElementChild;
        if (observedChild && observedChild !== nextChild) {
          resizeObserver.unobserve(observedChild);
          observedChild = null;
        }
        if (nextChild && nextChild !== observedChild) {
          resizeObserver.observe(nextChild);
          observedChild = nextChild;
        }
        measureScroll();
      };

      syncObservedChild();

      const mutationObserver = new MutationObserver(syncObservedChild);
      mutationObserver.observe(el, { childList: true });
      el.addEventListener('scroll', measureScroll, { passive: true });

      cleanupRef.current = () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        el.removeEventListener('scroll', measureScroll);
        observedChild = null;
      };
    },
    [measureScroll]
  );

  const handleToggleScroll = () => {
    const el = viewportRef.current;
    if (!el) return;

    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    el.scrollTo({
      left: scrolledToEnd ? 0 : maxScrollLeft,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = blockRef.current;
    if (!el || typeof event.clientY !== 'number') return;

    const { clientY } = event;
    cancelPendingPointerRaf();

    pointerRafRef.current = requestAnimationFrame(() => {
      pointerRafRef.current = null;
      const rect = el.getBoundingClientRect();
      const y = Math.max(
        BUTTON_HALF_HEIGHT,
        Math.min(clientY - rect.top, rect.height - BUTTON_HALF_HEIGHT)
      );
      if (Number.isNaN(y)) return;
      el.style.setProperty(SCROLL_BTN_Y_VAR, `${y}px`);
    });
  };

  return (
    <ScrollBlock ref={setBlockNode} onPointerMove={handlePointerMove}>
      {hasOverflow ? (
        <ScrollToggleButton
          type="button"
          $side={scrolledToEnd ? 'left' : 'right'}
          onClick={handleToggleScroll}
          aria-label={
            scrolledToEnd ? scrollBackAriaLabel : scrollForwardAriaLabel
          }
        >
          {scrolledToEnd ? (
            <AllieChevronBackOutline allieColor="white" />
          ) : (
            <AllieChevronForwardOutline allieColor="white" />
          )}
        </ScrollToggleButton>
      ) : null}
      <ScrollViewport
        ref={setViewportNode}
        data-testid="datagrid-horizontal-scroll-viewport"
      >
        {children}
      </ScrollViewport>
    </ScrollBlock>
  );
};
